import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Label } from "reactstrap";
import "../../assets/scss/custom/components/CustomSelector.scss";
import chevronDownIcon from "../../assets/images/support/chevron-down.svg";
import closeIcon from "../../assets/images/user-dashboard/remove-circle.svg";

// Add style to hide scrollbar for WebKit browsers
const style = document.createElement("style");
style.textContent = `
  .custom-multi-selector-value::-webkit-scrollbar {
    display: none;
  }
  .custom-multi-selector-value {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
if (!document.head.querySelector("style[data-custom-multi-selector]")) {
  style.setAttribute("data-custom-multi-selector", "true");
  document.head.appendChild(style);
}

/**
 * CustomMultiSelector
 * Reusable multi-select dropdown styled to match the Omaha-style auth screens.
 *
 * Usage:
 * <CustomMultiSelector
 *   label="Select Restaurants"
 *   name="restaurants"
 *   placeholder="Select restaurants"
 *   options={[
 *     { value: 1, label: "Restaurant 1" },
 *     { value: 2, label: "Restaurant 2" }
 *   ]}
 *   value={[1, 2]}
 *   onChange={(values) => setFieldValue("restaurants", values)}
 *   invalid={touched.restaurants && !!errors.restaurants}
 *   errorText={errors.restaurants}
 *   loading={loading}
 *   onLoadMore={handleLoadMore}
 *   hasMore={hasMore}
 * />
 */
const CustomMultiSelector = ({
  label,
  name,
  id,
  placeholder,
  options = [],
  value = [],
  onChange,
  invalid,
  errorText,
  optional,
  className,
  disabled,
  loading = false,
  onLoadMore,
  hasMore = false,
  ...rest
}) => {
  const selectorId = id || name;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      setSearchTerm("");
      if (!isOpen && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const handleSelect = (optionValue, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !loading &&
      onLoadMore
    ) {
      onLoadMore();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div
      className={`custom-selector-wrapper ${className || ""}`}
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <div
        className={`custom-selector-group ${invalid ? "is-invalid" : ""} ${disabled ? "is-disabled" : ""}`}
      >
        {label && (
          <Label htmlFor={selectorId} className="custom-selector-label">
            {label}
            {optional && (
              <span className="custom-selector-optional"> (Optional)</span>
            )}
          </Label>
        )}
        <div
          className={`custom-selector-field ${isOpen ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${selectorId}-dropdown`}
          aria-disabled={disabled}
        >
          <div
            className="custom-selector-value custom-multi-selector-value"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            id={`${selectorId}-value`}
          >
            {selectedOptions.length === 0 ? (
              <span className="custom-selector-placeholder">{placeholder}</span>
            ) : (
              <div
                className="custom-multi-selector-tags"
                style={{ display: "inline-flex", gap: "6px" }}
              >
                {selectedOptions.map((option) => (
                  <div
                    key={option.value}
                    className="custom-multi-selector-tag"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 10px",
                      backgroundColor: "#f97316",
                      color: "#fff",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    <span className="custom-multi-selector-tag-label">
                      {option.label}
                    </span>
                    <button
                      type="button"
                      className="custom-multi-selector-tag-remove"
                      onClick={(e) =>
                        !disabled && handleRemove(option.value, e)
                      }
                      aria-label={`Remove ${option.label}`}
                      disabled={disabled}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "0",
                        cursor: disabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        opacity: disabled ? 0.5 : 0.8,
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        !disabled && (e.currentTarget.style.opacity = "1")
                      }
                      onMouseLeave={(e) =>
                        !disabled && (e.currentTarget.style.opacity = "0.8")
                      }
                    >
                      <img
                        src={closeIcon}
                        alt="Remove"
                        style={{ width: "12px", height: "12px" }}
                      />
                    </button>
                  </div>
                ))}
                {selectedOptions.length > 0 && (
                  <button
                    type="button"
                    className="custom-multi-selector-clear"
                    onClick={(e) => !disabled && handleClearAll(e)}
                    aria-label="Clear all"
                    disabled={disabled}
                    style={{
                      padding: "4px 10px",
                      backgroundColor: disabled ? "#f3f4f6" : "#e5e7eb",
                      color: disabled ? "#9ca3af" : "#374151",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "11px",
                      fontWeight: "500",
                      cursor: disabled ? "not-allowed" : "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      !disabled &&
                      (e.currentTarget.style.backgroundColor = "#d1d5db")
                    }
                    onMouseLeave={(e) =>
                      !disabled &&
                      (e.currentTarget.style.backgroundColor = "#e5e7eb")
                    }
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="custom-selector-icon">
            {loading ? (
              <div className="custom-selector-spinner" />
            ) : (
              <img
                src={chevronDownIcon}
                alt="Toggle dropdown"
                className={isOpen ? "rotate-180" : ""}
              />
            )}
          </div>
        </div>
        {isOpen && !disabled && (
          <div
            className="custom-selector-dropdown custom-multi-selector-dropdown"
            role="listbox"
            id={`${selectorId}-dropdown`}
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            <div className="custom-selector-search">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div
              className="custom-selector-options custom-multi-selector-options"
              ref={listRef}
              onScroll={handleScroll}
              style={{ maxHeight: "120px", overflowY: "auto" }}
            >
              {filteredOptions.length === 0 ? (
                <div className="custom-selector-no-results">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`custom-selector-option custom-multi-selector-option ${
                      value.includes(option.value) ? "is-selected" : ""
                    }`}
                    onClick={(e) => handleSelect(option.value, e)}
                    role="option"
                    aria-selected={value.includes(option.value)}
                    style={{
                      padding: "10px 15px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div className="custom-multi-selector-option-content">
                      <span className="custom-multi-selector-option-label">
                        {option.label}
                      </span>
                      {value.includes(option.value) && (
                        <span className="custom-multi-selector-option-check">
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div
                  className="custom-selector-loading"
                  style={{ paddingLeft: "15px" }}
                >
                  ...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {invalid && errorText && (
        <div className="custom-selector-feedback show">{errorText}</div>
      )}
    </div>
  );
};

CustomMultiSelector.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
  errorText: PropTypes.string,
  optional: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
};

export default CustomMultiSelector;
